import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

interface FAQAnswerProps {
  content: string;
  className?: string;
}

/**
 * Normalize list markers for proper Markdown rendering:
 * - "*Item" or "-Item" at line start -> "* Item" / "- Item" (space after bullet)
 * - "1.Item" -> "1. Item" for ordered lists
 * - " *Item" or " -Item" mid-line -> newline + bullet (split into list lines)
 */
function normalizeListMarkers(text: string): string {
  return text
    .replace(/(^|\n)([-*+])([A-Za-z])/g, "$1$2 $3")
    .replace(/(^|\n)(\d+\.)([A-Za-z])/g, "$1$2 $3")
    .replace(/([ \t])([-*+])([A-Z])/g, "\n$2 $3");
}

export function FAQAnswer({ content, className }: FAQAnswerProps) {
  const normalized = normalizeListMarkers(content);
  return (
    <div className={cn("faq-answer text-muted-foreground", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          p: ({ children }) => (
            <p className="mb-2 last:mb-0">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="leading-relaxed">{children}</li>
          ),
        }}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}
